import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../config/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);

  console.log('AuthProvider rendering - loading:', loading, 'user:', user?.email, 'user ID:', user?.id, 'error:', error);

  // Load user role from Supabase Auth metadata
  const loadUserRole = async (user) => {
    if (!user) {
      setUserRole(null);
      return;
    }
    
    try {
      // Rol bilgisini user metadata'dan al
      let role = user.user_metadata?.role;
      
      // Eğer rol yoksa, default rol ata ve güncelle
      if (!role) {
        console.log('No role found in user metadata, setting default role to admin');
        role = 'admin'; // İlk kullanıcı için admin rolü
        
        // Rol bilgisini user metadata'ya kaydet
        const { error: updateError } = await supabase.auth.updateUser({
          data: { role: role }
        });
        
        if (updateError) {
          console.error('Error updating user role:', updateError);
        } else {
          console.log('User role updated to admin');
        }
      }
      
      setUserRole(role);
    } catch (error) {
      console.error('Error loading user role:', error);
      setUserRole('admin'); // Default role
    }
  };

  // Check if user is logged in on app start
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Get initial session
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Auth session error:', error);
          setError('Authentication error: ' + error.message);
        } else {
          setSession(session);
          setUser(session?.user ?? null);
          if (session?.user) {
            await loadUserRole(session.user);
          }
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
        setError('Failed to initialize authentication');
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event, 'User email:', session?.user?.email, 'User ID:', session?.user?.id);
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        await loadUserRole(session.user);
      } else {
        setUserRole(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async (credentials) => {
    try {
      setError(null);
      setLoading(true);
      
      const { error } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password
      });

      if (error) {
        setError(error.message);
        return { success: false, error: error.message };
      }

      // User and session will be set automatically by the auth state change listener
      return { success: true };
    } catch (err) {
      const errorMessage = err.message || 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      // First clear local state to prevent UI issues
      setUser(null);
      setUserRole(null);
      setSession(null);

      const { error } = await supabase.auth.signOut();

      // AuthSessionMissingError is actually OK - it means session was already cleared
      if (error && error.message !== 'Auth session missing!') {
        console.error('Logout error:', error);
        throw error;
      }

      // User and session will be cleared automatically by the auth state change listener
      setError(null);
      return { success: true };
    } catch (err) {
      console.error('Logout error:', err);
      // Don't show error for AuthSessionMissingError since it's expected
      if (err.message !== 'Auth session missing!') {
        setError('Çıkış yapılırken hata oluştu');
      }
      // Still return success for AuthSessionMissingError
      return { success: true };
    }
  };

  const signUp = async (credentials) => {
    try {
      setError(null);
      setLoading(true);
      
      const { error } = await supabase.auth.signUp({
        email: credentials.email,
        password: credentials.password,
        options: {
          data: credentials.metadata || {}
        }
      });

      if (error) {
        setError(error.message);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (err) {
      const errorMessage = err.message || 'Sign up failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const isAuthenticated = !!user;

  // Enhanced permission check functions
  const hasPermission = (permission) => {
    if (!userRole) return false;
    
    const permissions = {
      admin: [
        'dashboard', 'customers', 'products', 'settings', 'users', 'reports', 'transactions',
        'customers:create', 'customers:read', 'customers:update', 'customers:delete',
        'products:create', 'products:read', 'products:update', 'products:delete',
        'products:view_prices', 'products:edit_prices',
        'users:create', 'users:read', 'users:update', 'users:delete',
        'reports:view', 'reports:export'
      ],
      warehouse: [
        'dashboard', 'products', 'transactions',
        'products:create', 'products:read', 'products:update', 'products:delete',
        'products:view_prices' // Depo sadece fiyatları görebilir, düzenleyemez
      ],
      reception: [
        'dashboard', 'customers',
        'customers:create', 'customers:read', 'customers:update', 'customers:delete'
      ]
    };
    
    return permissions[userRole]?.includes(permission) || false;
  };

  // Specific permission checks
  const canCreateCustomer = () => hasPermission('customers:create');
  const canReadCustomer = () => hasPermission('customers:read');
  const canUpdateCustomer = () => hasPermission('customers:update');
  const canDeleteCustomer = () => hasPermission('customers:delete');

  const canCreateProduct = () => hasPermission('products:create');
  const canReadProduct = () => hasPermission('products:read');
  const canUpdateProduct = () => hasPermission('products:update');
  const canDeleteProduct = () => hasPermission('products:delete');
  const canViewProductPrices = () => hasPermission('products:view_prices');
  const canEditProductPrices = () => hasPermission('products:edit_prices');

  const canManageUsers = () => hasPermission('users:create') || hasPermission('users:update') || hasPermission('users:delete');
  const canViewReports = () => hasPermission('reports:view');

  const isAdmin = () => userRole === 'admin';
  const isWarehouse = () => userRole === 'warehouse';
  const isReception = () => userRole === 'reception';

  const value = {
    user,
    userRole,
    session,
    loading,
    error,
    login,
    logout,
    signUp,
    isAuthenticated,
    hasPermission,
    isAdmin,
    isWarehouse,
    isReception,
    // Customer permissions
    canCreateCustomer,
    canReadCustomer,
    canUpdateCustomer,
    canDeleteCustomer,
    // Product permissions
    canCreateProduct,
    canReadProduct,
    canUpdateProduct,
    canDeleteProduct,
    canViewProductPrices,
    canEditProductPrices,
    // User management permissions
    canManageUsers,
    canViewReports,
    setError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
